import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { RepairCostingInfosManagement } from '../components/RepairCostingInfosManagement';
import {
  TRepairCostingSchema,
  repairCostingSchema,
} from '@/schemas/repair-costing/repair-costing.schema';
import {
  useGetRepairCostingById,
  useUpdateRepairCosting,
} from '@/hooks/repair-costing.hooks';
import { useTranslation } from 'react-i18next';

export const EditRepairCostingPage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetRepairCostingById(`${id}`);
  const { t } = useTranslation('repairCosting');
  const navigate = useNavigate();

  const {
    watch: watchRepairCosting,
    control: repairCostingControl,
    register: registerRepairCosting,
    setValue: setRepairCostingValue,
    setError: setRepairCostingError,
    handleSubmit: handleSubmitRepairCosting,
    formState: {
      errors: repairCostingErrors,
      isSubmitting: isSubmittingRepairCosting,
    },
  } = useForm<TRepairCostingSchema>({
    resolver: zodResolver(repairCostingSchema),
  });

  const {
    mutate: mutateUpdateRepairCosting,
    isPending: isPendingUpdateRepairCosting,
  } = useUpdateRepairCosting({
    t,
    handleSuccess: () => navigate('/repair-costings'),
  });

  const onSubmitRepairCostingInfos = (repairCosting: TRepairCostingSchema) => {
    const repairCostingWithId = { id, ...repairCosting };
    mutateUpdateRepairCosting(repairCostingWithId);
  };

  return (
    <>
      <PageHeader
        backToLink="/repair-costings"
        title={t('theRepairCosting')}
        subTitleIconClassName="ki-paintbucket"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('repairCostingList')}
              linkTo="/repair-costings"
            />
            <BreadcrumbItem text={t('theRepairCosting')} active />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('repairCostingNotFound')}...`} />
      ) : (
        <div>
          <RepairCostingInfosManagement<TRepairCostingSchema>
            t={t}
            data={!isLoading && data?.data}
            watch={watchRepairCosting}
            control={repairCostingControl}
            register={registerRepairCosting}
            setValue={setRepairCostingValue}
            setError={setRepairCostingError}
            errors={repairCostingErrors}
            submitDisabled={
              isSubmittingRepairCosting || isPendingUpdateRepairCosting
            }
            onSubmit={handleSubmitRepairCosting(onSubmitRepairCostingInfos)}
          />
        </div>
      )}
      {(isLoading ||
        isSubmittingRepairCosting ||
        isPendingUpdateRepairCosting) && <Loader />}
    </>
  );
};
